import React from 'react';
import { ArrowForward, Check, Info, Warning } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Return, ReturnStatus, translateReturnStatus } from '../../../models/Return';

type Props = {
  return: Return;
  url?: string;
};

function ReturnCard({ return: returnData, url }: Props) {
  const totalItems = returnData.returnItems?.reduce(
    (sum, item) => sum + item.quantity,
    0,
  ) || 0;

  const getReturnStatusColor = (status: ReturnStatus) => {
    switch (status) {
      case ReturnStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-200';
      case ReturnStatus.PENDING:
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case ReturnStatus.REJECTED:
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-amber-50 text-amber-700 border-amber-200';
    }
  };

  const getReturnStatusIcon = (status: ReturnStatus) => {
    switch (status) {
      case ReturnStatus.COMPLETED:
        return <Check fontSize="small" />;
      case ReturnStatus.REJECTED:
        return <Warning fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <motion.div
      className={`bg-white rounded-lg border ${
        returnData.status === ReturnStatus.REJECTED
          ? 'border-red-200'
          : 'border-gray-200'
      } overflow-hidden hover:shadow-md transition-shadow`}
      whileHover={{ y: -2 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-medium text-gray-900 text-base">
              Zwrot {returnData.returnNumber}
            </h3>
            <p className="text-sm text-gray-500">
              {formatDate(returnData.createdAt)}
            </p>
          </div>

          <div
            className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 border ${getReturnStatusColor(
              returnData.status,
            )}`}
          >
            {getReturnStatusIcon(returnData.status)}
            {translateReturnStatus(returnData.status)}
          </div>
        </div>

        <div className="flex justify-between items-end mb-3">
          <div className="flex items-center">
            <div className="flex -space-x-2 mr-2">
              {returnData.returnItems?.slice(0, 3).map((item) => (
                <div
                  key={item.id}
                  className="w-8 h-8 border-2 border-white rounded-full overflow-hidden bg-gray-100"
                >
                  {item.orderItem?.product?.mobilePrimaryImage ? (
                    <img
                      src={item.orderItem.product.mobilePrimaryImage}
                      alt={item.orderItem.productName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xs text-gray-500">?</span>
                    </div>
                  )}
                </div>
              ))}

              {returnData.returnItems && returnData.returnItems.length > 3 && (
                <div className="w-8 h-8 border-2 border-white rounded-full bg-gray-50 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-600">
                    +{returnData.returnItems.length - 3}
                  </span>
                </div>
              )}
            </div>

            <span className="text-sm text-gray-600">
              {totalItems}{' '}
              {totalItems === 1
                ? 'produkt'
                : totalItems < 5
                  ? 'produkty'
                  : 'produktów'}
            </span>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-3 mt-3 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            <p>Zamówienie: {returnData.order?.orderNumber}</p>
          </div>

          <Link
            to={url || `${returnData.id}`}
            className="flex items-center text-primary font-medium hover:text-primary-dark transition-colors text-sm"
          >
            Szczegóły
            <ArrowForward fontSize="small" className="ml-1" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default ReturnCard;
